// Raw fetch + manual SSE parsing for the assistant chat endpoint. The shared
// ApiClient can't stream, so this talks to the API directly, mirroring its
// auth + one-time 401 retry behavior.
import { getAccessToken, refreshAccessToken } from '@/lib/supabase';
import { workspacePath } from '@wrapforce/shared/api-client';
import type { AssistantUsage, ChatMessage, PendingAction } from './types';

const MAX_MESSAGES = 30;
const MAX_CONTENT_LENGTH = 4000;

export class AssistantStreamError extends Error {
  code: string;
  constructor(code: string, message?: string) {
    super(message ?? code);
    this.name = 'AssistantStreamError';
    this.code = code;
  }
}

function trimMessages(messages: ChatMessage[]): ChatMessage[] {
  return messages.slice(-MAX_MESSAGES).map((m) => ({ role: m.role, content: m.content.slice(0, MAX_CONTENT_LENGTH) }));
}

interface ParsedEventBlock {
  event: string;
  data: string;
}

function parseEventBlock(raw: string): ParsedEventBlock | null {
  let event = 'message';
  let data = '';
  for (const line of raw.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) data += line.slice(5).trim();
  }
  return data ? { event, data } : null;
}

export async function streamChat(opts: {
  workspaceId: string;
  messages: ChatMessage[];
  signal: AbortSignal;
  onText: (delta: string) => void;
  onTool: (name: string) => void;
}): Promise<{ message: string; actions: PendingAction[]; usage?: AssistantUsage }> {
  const baseUrl = import.meta.env.VITE_API_URL as string;
  const path = workspacePath(opts.workspaceId, '/assistant/chat');
  const body = JSON.stringify({ messages: trimMessages(opts.messages) });

  const doFetch = async (isRetry: boolean): Promise<Response> => {
    const token = await getAccessToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(`${baseUrl}${path}`, { method: 'POST', headers, body, signal: opts.signal });
    if (res.status === 401 && !isRetry) {
      const newToken = await refreshAccessToken();
      if (newToken) return doFetch(true);
    }
    return res;
  };

  const res = await doFetch(false);

  if (!res.ok || !res.body) {
    let code = 'INTERNAL';
    let message: string | undefined;
    try {
      const envelope = (await res.json()) as { error?: { code?: string; message?: string } };
      if (envelope?.error?.code) code = envelope.error.code;
      message = envelope?.error?.message;
    } catch {
      // no JSON body - fall back to the generic INTERNAL code.
    }
    throw new AssistantStreamError(code, message);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let message = '';
  let actions: PendingAction[] = [];
  let usage: AssistantUsage | undefined;

  const handleBlock = (raw: string) => {
    const parsed = parseEventBlock(raw);
    if (!parsed) return;
    let payload: unknown;
    try {
      payload = JSON.parse(parsed.data);
    } catch {
      return;
    }
    if (parsed.event === 'text') {
      opts.onText((payload as { delta?: string }).delta ?? '');
    } else if (parsed.event === 'tool') {
      opts.onTool((payload as { name?: string }).name ?? '');
    } else if (parsed.event === 'done') {
      const done = payload as { message?: string; actions?: PendingAction[]; usage?: AssistantUsage };
      message = done.message ?? '';
      actions = done.actions ?? [];
      usage = done.usage;
    } else if (parsed.event === 'error') {
      throw new AssistantStreamError((payload as { code?: string }).code ?? 'INTERNAL');
    }
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx = buffer.indexOf('\n\n');
    while (idx !== -1) {
      const raw = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      if (raw.trim()) handleBlock(raw);
      idx = buffer.indexOf('\n\n');
    }
  }

  return { message, actions, usage };
}
